import { MedplumClient } from '@medplum/core';
import { Patient, Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { DocumentStatus, FormCategory, FormCompletionStats } from '../types/documents';

export class DocumentService {
  /**
   * Fetches assigned forms for a patient
   * @param medplum The Medplum client instance
   * @param patient The patient to fetch forms for
   * @returns Array of DocumentStatus objects representing assigned forms
   */
  static async getAssignedForms(medplum: MedplumClient, patient: Patient): Promise<DocumentStatus[]> {
    if (!patient.id) {
      console.error('Patient ID is required to fetch assigned forms');
      return [];
    }

    try {
      // Search for QuestionnaireResponse resources with form-assignment extension
      console.log(`Searching for forms for patient: ${patient.id}`);
      
      // Try general search first to debug what's available
      let searchResult = await medplum.search('QuestionnaireResponse', {
        _count: 100,
        subject: `Patient/${patient.id}`
      });
      
      console.log(`Found ${searchResult.entry?.length || 0} forms in general search`);
      
      // Examine available QuestionnaireResponses more closely
      if (searchResult.entry && searchResult.entry.length > 0) {
        searchResult.entry.forEach((entry, index) => {
          const resource = entry.resource as QuestionnaireResponse;
          console.log(`QuestionnaireResponse ${index + 1}: ID=${resource.id}`);
          console.log(`  Questionnaire: ${resource.questionnaire || 'NONE'}`);
          console.log(`  Status: ${resource.status || 'NONE'}`);
          console.log(`  Extensions: ${resource.extension?.length || 0}`);
          if (resource.extension?.length) {
            resource.extension.forEach(ext => {
              console.log(`    URL: ${ext.url}, Value: ${JSON.stringify(ext.valueBoolean || ext.valueCode || ext.valueInteger || 'complex')}`);
            });
          }
        });
      }
      
      // If no results at all, return empty
      if (!searchResult.entry || searchResult.entry.length === 0) {
        console.log('No forms found for patient');
        return [];
      }

      const assignedForms: DocumentStatus[] = [];
      
      // Process each QuestionnaireResponse to convert to DocumentStatus
      for (const entry of searchResult.entry) {
        const resource = entry.resource as QuestionnaireResponse;
        console.log(`Processing QuestionnaireResponse: ${resource.id}`);
        
        // Assume all forms are portal visible unless explicitly marked otherwise
        const portalVisibleExt = resource.extension?.find(
          ext => ext.url === 'https://progressnotes.app/fhir/StructureDefinition/portal-visible'
        );
        
        const isPortalVisible = portalVisibleExt ? portalVisibleExt.valueBoolean !== false : true;
        
        // Log visibility
        if (!isPortalVisible) {
          console.log(`Skipping form ${resource.id} - not portal visible`);
          continue;
        }
          
        // Get form category - default to questionnaire if not found
        const categoryExt = resource.extension?.find(
          ext => ext.url === 'https://progressnotes.app/fhir/StructureDefinition/form-category'
        );
        const category = categoryExt?.valueCode as FormCategory || 'questionnaire';
        console.log(`Form ${resource.id} category: ${category}`);
        
        // Get display order for sorting
        const displayOrderExt = resource.extension?.find(
          ext => ext.url === 'https://progressnotes.app/fhir/StructureDefinition/display-order'
        );
        const displayOrder = displayOrderExt?.valueInteger || 999;
        
        // Check if required - default to true if extension not found
        const requiredExt = resource.extension?.find(
          ext => ext.url === 'https://progressnotes.app/fhir/StructureDefinition/required-form'
        );
        const isRequired = requiredExt ? requiredExt.valueBoolean === true : true;
        
        // Get form status
        const formStatus = resource.status === 'completed' ? 'completed' : 'not-started';
        
        // Extract questionnaire reference details
        let questionnaireId = '';
        let formTitle = 'Untitled Form';
        
        if (resource.questionnaire) {
          console.log(`Form ${resource.id} has questionnaire reference: ${resource.questionnaire}`);
          const parts = resource.questionnaire.split('/');
          
          // Only work with Questionnaire resources now, not DocumentReference
          if (parts[0] === 'Questionnaire') {
            questionnaireId = parts[1];
            
            // Try to get the form title
            try {
              console.log(`Fetching Questionnaire/${questionnaireId}`);
              const questionnaire = await medplum.readResource('Questionnaire', questionnaireId);
              formTitle = questionnaire.title || questionnaire.name || 'Untitled Form';
              console.log(`Got Questionnaire title: ${formTitle}`);
            } catch (error) {
              console.error(`Error fetching form details for Questionnaire/${questionnaireId}:`, error);
              // Use resource ID as fallback title
              formTitle = `Form ${resource.id}`;
            }
          } else {
            // For DocumentReference references, just use a generic title
            console.log(`Form references a DocumentReference - using generic title`);
            formTitle = (category === 'consent') ? 'Consent Form' : 'Form Document';
          }
        } else {
          console.log(`Form ${resource.id} has no questionnaire reference, using default title`);
          formTitle = `Form ${resource.id}`;
        }
        
        // Map category to route and generate URL
        let route = '';
        switch (category) {
          case 'questionnaire':
            route = 'intake';
            break;
          case 'consent':
            route = 'consent';
            break;
          case 'assessment':
            route = 'screening'; // Use dedicated component for assessments
            break;
          case 'document':
            route = 'view'; // Generic document viewer
            break;
          default:
            route = 'intake';
        }
        
        const href = `/documents/forms/${route}?id=${resource.id}`;
        
        // Create DocumentStatus object
        assignedForms.push({
          id: resource.id!,
          name: formTitle,
          href,
          status: formStatus,
          required: isRequired,
          resourceId: resource.id!,
          displayOrder,
          category,
          questionnaireType: 'Questionnaire',
          questionnaireId,
          responseId: resource.id
        });
      }
      
      // Sort by display order
      assignedForms.sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));
      
      return assignedForms;
    } catch (error) {
      console.error('Error fetching assigned forms:', error);
      return [];
    }
  }
  
  /**
   * Gets form completion statistics
   * @param forms Array of DocumentStatus objects
   * @returns Statistics about form completion
   */
  static getCompletionStats(forms: DocumentStatus[]): FormCompletionStats {
    const total = forms.length;
    const completed = forms.filter(form => form.status === 'completed').length;
    const inProgress = forms.filter(form => form.status === 'pending').length;
    const notStarted = forms.filter(form => form.status === 'not-started').length;
    
    return {
      total,
      completed,
      inProgress,
      notStarted
    };
  }

  /**
   * Loads a specific form by ID
   * @param medplum The Medplum client
   * @param formId The QuestionnaireResponse ID
   * @returns The form details and associated questionnaire
   */
  static async getFormById(medplum: MedplumClient, formId: string): Promise<{
    response: QuestionnaireResponse | any;
    questionnaire?: Questionnaire;
  }> {
    console.log(`DEBUG: Getting form by ID: ${formId}`);
    
    try {
      // Load the QuestionnaireResponse
      console.log(`DEBUG: Calling medplum.readResource('QuestionnaireResponse', '${formId}')`);
      let response;
      
      try {
        response = await medplum.readResource('QuestionnaireResponse', formId);
        console.log('DEBUG: Found QuestionnaireResponse:', response);
        
      } catch (readErr) {
        // Get more details about the error
        const errorMsg = readErr instanceof Error ? readErr.message : 'Unknown error';
        console.error(`DEBUG: Error reading QuestionnaireResponse: ${errorMsg}`, readErr);
        
        if (errorMsg.includes('Not Found')) {
          console.error('DEBUG: Resource not found - it may not exist or you may not have permission to access it');
          
          // Try a search to see if we can find any clues
          console.log('DEBUG: Trying a search as fallback');
          const searchResult = await medplum.search('QuestionnaireResponse', {
            '_id': formId
          });
          
          console.log(`DEBUG: Search found ${searchResult.entry?.length || 0} results`);
          
          if (searchResult.entry && searchResult.entry.length > 0) {
            response = searchResult.entry[0].resource as QuestionnaireResponse;
            console.log('DEBUG: Using first search result as fallback');
          } else {
            // Rethrow the original error if we couldn't find anything
            throw readErr;
          }
        } else {
          // For other errors, rethrow
          throw readErr;
        }
      }
      
      // Load the associated questionnaire
      let questionnaire: Questionnaire | undefined;
      
      if (response.questionnaire) {
        console.log(`Form has questionnaire reference: ${response.questionnaire}`);
        const parts = response.questionnaire.split('/');
        
        // Only support Questionnaire resources now
        if (parts[0] === 'Questionnaire') {
          const id = parts[1];
          
          try {
            questionnaire = await medplum.readResource('Questionnaire', id);
            console.log(`Loaded referenced Questionnaire:`, questionnaire);
          } catch (error) {
            console.error(`Error loading Questionnaire/${id}:`, error);
            
            // If the referenced questionnaire doesn't exist, create a replacement
            try {
              console.log(`Creating replacement Questionnaire for missing ${id}`);
              
              // Standard replacement for missing questionnaires
              console.error(`RESOURCE ACCESS ERROR: Unable to access Questionnaire/${id}`);
              
              // Create a more informative fallback form
              questionnaire = await medplum.createResource({
                resourceType: 'Questionnaire',
                status: 'active',
                title: `Fallback Form - Access Error QACCESS-02`,
                item: [
                  {
                    linkId: 'content',
                    text: '⚠️ Form Access Error: The original questionnaire could not be found. Below is a default consent form.',
                    type: 'display',
                    extension: [
                      {
                        url: "http://hl7.org/fhir/StructureDefinition/rendering-xhtml",
                        valueString: `
                        <h3>DEFAULT CONSENT FORM - ACCESS ERROR</h3>
                        <p>The original consent form could not be loaded. Please contact support for assistance.</p>
                        <p>This is a default replacement consent form that allows you to proceed with your intake process.</p>
                        
                        <h4>INFORMED CONSENT FOR THERAPY SERVICES</h4>
                        <p>This document contains important information about our professional services and business policies. By agreeing below, you indicate your understanding and acceptance of these terms.</p>
                        
                        <h4>CONFIDENTIALITY</h4>
                        <p>The law protects the privacy of all communications between a client and a therapist. In most situations, we can only release information about your treatment to others if you sign a written authorization form. There are some situations in which we are legally obligated to take action to protect others from harm, even if we have to reveal some information about a client's treatment.</p>
                        `
                      }
                    ]
                  },
                  {
                    linkId: 'agreement',
                    text: 'Patient Agreement',
                    type: 'boolean',
                    required: true
                  },
                  {
                    linkId: 'signature-datetime',
                    text: 'Signed On',
                    type: 'dateTime',
                    required: false
                  }
                ],
                extension: [
                  {
                    url: "https://progressnotes.app/fhir/StructureDefinition/form-fallback",
                    valueBoolean: true
                  },
                  {
                    url: "https://progressnotes.app/fhir/StructureDefinition/error-code",
                    valueString: "QACCESS-02"
                  }
                ]
              });
              console.log('Created replacement questionnaire:', questionnaire);
            } catch (createError) {
              console.error('Error creating replacement questionnaire:', createError);
              // Fall back to undefined, will be handled by the caller
            }
          }
        } else {
          // For DocumentReference references, create a temporary Questionnaire
          console.log('Converting DocumentReference reference to a Questionnaire');
          
          try {
            // Create a temporary Questionnaire for consent form
            questionnaire = await medplum.createResource({
              resourceType: 'Questionnaire',
              status: 'active',
              title: 'Migrated Consent Form',
              item: [
                {
                  linkId: 'content',
                  text: 'Consent Form Content',
                  type: 'display',
                  extension: [
                    {
                      url: "http://hl7.org/fhir/StructureDefinition/rendering-xhtml",
                      valueString: `
                      <h3>MIGRATED CONSENT FORM</h3>
                      <p>This consent form has been migrated from the legacy system. The original content could not be loaded.</p>
                      
                      <h4>INFORMED CONSENT FOR THERAPY SERVICES</h4>
                      <p>This document contains important information about our professional services and business policies. By agreeing below, you indicate your understanding and acceptance of these terms.</p>
                      
                      <h4>CONFIDENTIALITY</h4>
                      <p>The law protects the privacy of all communications between a client and a therapist. In most situations, we can only release information about your treatment to others if you sign a written authorization form. There are some situations in which we are legally obligated to take action to protect others from harm, even if we have to reveal some information about a client's treatment.</p>
                      `
                    }
                  ]
                },
                {
                  linkId: 'agreement',
                  text: 'Patient Agreement',
                  type: 'boolean',
                  required: true
                },
                {
                  linkId: 'signature-datetime',
                  text: 'Signed On',
                  type: 'dateTime',
                  required: false
                }
              ],
              extension: [
                {
                  url: "https://progressnotes.app/fhir/StructureDefinition/form-category",
                  valueCode: "consent"
                },
                {
                  url: "https://progressnotes.app/fhir/StructureDefinition/form-migration",
                  valueBoolean: true
                }
              ]
            });
            console.log('Created migration questionnaire:', questionnaire);
          } catch (createError) {
            console.error('Error creating migration questionnaire:', createError);
          }
        }
      } else {
        console.log('No questionnaire reference found');
      }
      
      return { response, questionnaire };
    } catch (error) {
      console.error(`Error getting form by ID ${formId}:`, error);
      throw error; // Rethrow to be handled by caller
    }
  }
  
  /**
   * Gets a form by the ID of the referenced Questionnaire
   * @param medplum The Medplum client
   * @param patient The patient
   * @param resourceId The ID of the referenced Questionnaire
   * @returns The QuestionnaireResponse and associated Questionnaire if found
   */
  static async getFormByResourceReference(
    medplum: MedplumClient,
    patient: Patient,
    resourceType: 'Questionnaire',
    resourceId: string
  ): Promise<{
    response: QuestionnaireResponse | null;
    questionnaire: Questionnaire | null;
  }> {
    console.log(`Searching for form referencing Questionnaire/${resourceId}`);
    
    try {
      // First get the referenced resource
      const questionnaire = await medplum.readResource('Questionnaire', resourceId);
      console.log(`Found referenced Questionnaire:`, questionnaire);
      
      // Search for QuestionnaireResponses that reference this resource
      const searchResult = await medplum.search('QuestionnaireResponse', {
        subject: `Patient/${patient.id}`,
        questionnaire: `Questionnaire/${resourceId}`
      });
      
      console.log(`Found ${searchResult.entry?.length || 0} responses referencing this resource`);
      
      if (searchResult.entry && searchResult.entry.length > 0) {
        const response = searchResult.entry[0].resource as QuestionnaireResponse;
        return { response, questionnaire };
      }
      
      // If no QuestionnaireResponse found, return the questionnaire but no response
      return { response: null, questionnaire };
    } catch (error) {
      console.error(`Error getting form by resource reference:`, error);
      return { response: null, questionnaire: null };
    }
  }

  /**
   * Checks if a questionnaire is a fallback form (created due to access issues)
   * @param questionnaire The questionnaire to check
   * @returns True if it's a fallback form, false otherwise
   */
  static isFallbackForm(questionnaire?: Questionnaire | null): boolean {
    if (!questionnaire) return false;
    
    // Check for the fallback extension
    const hasFallbackExtension = questionnaire.extension?.some(
      ext => ext.url === 'https://progressnotes.app/fhir/StructureDefinition/form-fallback' && 
             ext.valueBoolean === true
    );
    
    // Also check the title as a fallback
    const hasFallbackTitle = questionnaire.title?.includes('Fallback Form') || questionnaire.title?.includes('Migration') || false;
    
    return hasFallbackExtension || hasFallbackTitle;
  }
}